import { ChangeEvent, JSX, KeyboardEvent, useEffect, useMemo, useState } from "react";
import { WafflesPaginationProps } from "@app/shared/waffles/exports";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";

// Above this page count, render a numeric input instead of a <select> with one <option> per page.
// A <select> with hundreds of thousands of options can exhaust memory and crash the browser tab
// (observed with ~10M-row paginated tables in the AI assistant).
const PAGE_DROPDOWN_MAX = 1000;

const WafflesPagination = (props: WafflesPaginationProps): JSX.Element => {
  const [page, setPage] = useState(props.initialPage ?? 1);
  const [maxPage, setMaxPage] = useState(0);
  const [pageSize, setPageSize] = useState(props.recordsPerPage);
  const [pageInputValue, setPageInputValue] = useState("1");

  useEffect(() => {
    if (props.totalRecords && props.recordsPerPage > 0) {
      const numberOfPages = Math.ceil(props.totalRecords / props.recordsPerPage);
      setMaxPage(numberOfPages);
      // handle tabbed pagination where recordsPerPage may be different per tab
      setPageSize(props.recordsPerPage);
    }
  }, [props.recordsPerPage, props.totalRecords]);

  useEffect(() => {
    if (!props.enableBackendPagination)
    {
      if (props.skip !== (page - 1) * props.recordsPerPage) {
        setPage((props.skip / props.recordsPerPage) + 1);
      }
    }
  }, [props.skip, props.enableBackendPagination]);

  useEffect(() => {
    if (props.resetToFirstPage){
      props.changePage(1);
      setPage(1);
    }
  }, [props?.resetToFirstPage]);

  // Mirror page into the input so external changes (prev/next, page-size, reset) appear in the field.
  useEffect(() => {
    setPageInputValue(String(page));
  }, [page]);

  const useNumericInput = maxPage > PAGE_DROPDOWN_MAX;

  const pageDropdownOptions = useMemo(() => {
    if (useNumericInput) {
      return [];
    }
    const options: JSX.Element[] = [];
    for (let i = 0; i < maxPage; i += 1) {
      options.push(<option value={i + 1} key={i + 1}>{i + 1}</option>);
    }
    return options;
  }, [props.recordsPerPage, props.totalRecords, maxPage, useNumericInput]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > maxPage) {
      return false;
    }
    props.changePage(newPage);
    setPage(newPage);
  };

  const handlePageSizeChange = (recordsPerPage: number) => {
    setPageSize(recordsPerPage);
    //When the page size changes, reset to the first page
    setPage(1);
    props.handlePageSizeChange(recordsPerPage);
  };

  const commitPageInput = () => {
    const parsed = parseInt(pageInputValue, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > maxPage) {
      setPageInputValue(String(page));
      return;
    }
    if (parsed !== page) {
      handlePageChange(parsed);
    }
  };

  const handlePageInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    }
  };

  // If we are missing any of the required pagination props (changePage, recordsPerPage, or totalRecords), 
  // or if there is only 1 page and the props.showPageSizeSelector is false or undefined, we should not 
  // render the component, but return null instead.
  const isMissingRequiredPaginationProps = !props.changePage || !props.recordsPerPage || !props.totalRecords;
  const isSinglePageWithNoSizeSelector = maxPage === 1 && !props.showPageSizeSelector;

  if (isMissingRequiredPaginationProps || isSinglePageWithNoSizeSelector) return null;

  return (
    <div className={`waffles-pagination ${props.classNames}`}>
      <div className="waffles-pagination__content">
        <QESButton qesType="secondary" qesTestId="waffles-pagination__previous-page" onClick={() => handlePageChange(page - 1)} disabled={page <= 1}>Previous Page</QESButton>
        {(props.showPageSizeSelector && props.handlePageSizeChange && props?.pageSizeOptions?.length > 0) &&
          <span className="waffles-pagination__pages">
            Showing {(
              <QESSelect
                containerClassName="qes-select__label-padding-left"
                label=""
                hideLabel
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                testId="waffles-pagination__pages-select-page-size"
                value={pageSize}
              >
                {props.pageSizeOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </QESSelect>

            )}
          </span>
        }
        <span className="waffles-pagination__pages">
          Page {(
            useNumericInput ? (
              <input
                aria-label="Page number"
                className="waffles-pagination__page-input"
                data-testid="waffles-pagination__pages-input"
                max={maxPage}
                min={1}
                onBlur={commitPageInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPageInputValue(e.target.value)}
                onKeyDown={handlePageInputKeyDown}
                type="number"
                value={pageInputValue}
              />
            ) : (
              <QESSelect
                containerClassName="qes-select__label-padding"
                hideLabel={true}
                label=""
                onChange={(e) => handlePageChange(parseInt(e.target.value, 10))}
                testId="waffles-pagination__pages-select"
                value={page}
              >
                {pageDropdownOptions}
              </QESSelect>
            )
          )}
          of {Math.ceil(props.totalRecords / props.recordsPerPage)}
        </span>
        <QESButton qesType="secondary" qesTestId="waffles-pagination__next-page" onClick={() => handlePageChange(page + 1)} disabled={page >= maxPage}>Next Page</QESButton>
      </div>
    </div>
  );
};

export default WafflesPagination;
